import { HOUR_IN_MS } from '@/lib/constants/time';
import { IntegrationServices } from '@/modules/common/types/integrations';
import { logger } from '@/utils/logger/logger';

const TAG = 'OAuthState:';
const OAUTH_STATE_KEY = 'oauth_integration_state';

export interface OAuthState {
  integration: IntegrationServices;
  returnUrl: string;
  timestamp: number;
}

/**
 * Saves OAuth state to localStorage before redirecting to OAuth provider
 */
export const saveOAuthState = (integration: IntegrationServices, returnUrl: string): void => {
  const state: OAuthState = {
    integration,
    returnUrl,
    timestamp: Date.now(),
  };

  localStorage.setItem(OAUTH_STATE_KEY, JSON.stringify(state));

  logger.info('Saved OAuthState to localStorage', { integration, returnUrl });
};

/**
 * Retrieves OAuth state from localStorage after OAuth callback
 * Returns null if no state found or if state is expired (>1 hour)
 */
export const getOAuthState = (): OAuthState | null => {
  const stateJson = localStorage.getItem(OAUTH_STATE_KEY);

  if (!stateJson) {
    return null;
  }

  try {
    const state: OAuthState = JSON.parse(stateJson);

    // Check if state is expired (1 hour)
    const isExpired = Date.now() - state.timestamp > HOUR_IN_MS;

    if (isExpired) {
      clearOAuthState();
      return null;
    }

    return state;
  } catch {
    clearOAuthState();
    return null;
  }
};

/**
 * Clears OAuth state from localStorage
 */
export const clearOAuthState = (): void => {
  localStorage.removeItem(OAUTH_STATE_KEY);
  logger.info('Clearing OAuthState');
};
